<template>
  <!-- Patience meter: a chain of correct guesses fills the bar and climbs the XP multiplier; one
       wrong guess shatters it back to zero. Carries across puzzles within a run. -->
  <div class="momentum w-full max-w-sm mx-auto px-2 mb-2 select-none"
       :class="{ 'is-reset': resetting, 'is-active': multiplier > 1 }"
       :style="glowVars"
       title="Momentum: chain correct guesses to multiply your XP. A wrong guess resets it.">
    <div class="flex items-center gap-2">
      <span class="text-lg leading-none" :class="multiplier > 1 ? 'text-yellow-300 bolt-live' : 'text-gray-500'">⚡</span>
      <div class="relative flex-1 h-2.5 rounded-full bg-gray-700 overflow-hidden">
        <div class="absolute inset-y-0 left-0 rounded-full overflow-hidden transition-all duration-300 ease-out"
             :class="fillClass" :style="{ width: fillPct + '%' }">
          <div v-if="multiplier > 1" class="sheen"></div>
        </div>
      </div>
      <span class="text-sm font-extrabold tabular-nums whitespace-nowrap text-right transition-colors"
            :class="[multiplier > 1 ? labelClass : 'text-gray-500', { 'tier-pop': popping }]">×{{ multiplier }} XP</span>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {MOMENTUM_TIERS, nextMomentumThreshold} from "../composables/gameConstants.js";

const props = defineProps({
  momentum: {type: Number, default: 0},
  multiplier: {type: Number, default: 1},
});

// Fill the bar by progress through the current tier toward the next threshold (full at the cap).
const fillPct = computed(() => {
  const next = nextMomentumThreshold(props.momentum);
  if (next === null) return 100;
  // The current tier's floor is the highest threshold at or below the current momentum.
  const floor = MOMENTUM_TIERS.find(t => props.momentum >= t.threshold).threshold;
  return Math.min(100, ((props.momentum - floor) / (next - floor)) * 100);
});

const fillClass = computed(() => ({
  1: "bg-gray-500",
  1.5: "bg-gradient-to-r from-teal-500 to-teal-300",
  2: "bg-gradient-to-r from-green-500 to-green-300",
  2.5: "bg-gradient-to-r from-amber-500 to-yellow-300",
  3: "bg-gradient-to-r from-pink-500 via-red-500 to-orange-400",
}[props.multiplier] ?? "bg-gray-500"));

const labelClass = computed(() => ({
  1.5: "text-teal-300",
  2: "text-green-300",
  2.5: "text-yellow-300",
  3: "text-orange-300",
}[props.multiplier] ?? "text-gray-300"));

// The higher the multiplier, the more alive the meter gets: glow grows brighter and bigger while
// the pulse and sheen sweep speed up, so each tier feels more charged than the last. Driven by CSS
// vars so a single set of keyframes scales across all tiers.
const tier = computed(() => {
  const idx = MOMENTUM_TIERS.findIndex(t => props.multiplier === t.multiplier);
  return idx === -1 ? 0 : MOMENTUM_TIERS.length - 1 - idx;
});

const glowVars = computed(() => {
  const t = [
    {size: 0, speed: 2.2, color: "transparent"},
    {size: 7, speed: 1.9, color: "rgba(45, 212, 191, 0.55)"},
    {size: 10, speed: 1.5, color: "rgba(74, 222, 128, 0.6)"},
    {size: 14, speed: 1.1, color: "rgba(250, 204, 21, 0.65)"},
    {size: 20, speed: 0.7, color: "rgba(249, 115, 22, 0.8)"},
  ][tier.value];
  return {
    "--glow-size": `${t.size}px`,
    "--glow-color": t.color,
    "--pulse-speed": `${t.speed}s`,
  };
});

// Shatter the bar when momentum drops to zero from a chain, and pop the multiplier when it climbs
// a tier, so both the loss and the level-up are felt.
const resetting = ref(false);
const popping = ref(false);

watch(() => props.momentum, (now, was) => {
  if (was > 0 && now === 0) {
    resetting.value = false;
    requestAnimationFrame(() => { resetting.value = true; });
    setTimeout(() => { resetting.value = false; }, 500);
  }
});

watch(() => props.multiplier, (now, was) => {
  if (now > was) {
    popping.value = false;
    requestAnimationFrame(() => { popping.value = true; });
    setTimeout(() => { popping.value = false; }, 450);
  }
});
</script>

<style scoped>
/* Tier-scaled glow: the pulse grows and quickens via the CSS vars set per multiplier. */
.momentum.is-active {
  animation: glow-pulse var(--pulse-speed, 1.6s) ease-in-out infinite;
}

@keyframes glow-pulse {
  0%, 100% { filter: drop-shadow(0 0 calc(var(--glow-size, 0px) * 0.45) var(--glow-color, transparent)); }
  50% { filter: drop-shadow(0 0 var(--glow-size, 0px) var(--glow-color, transparent)); }
}

/* The bolt flickers in time with the glow, so it visibly speeds up at higher tiers. */
.bolt-live {
  animation: bolt-flicker var(--pulse-speed, 1.6s) ease-in-out infinite;
}

@keyframes bolt-flicker {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.22); opacity: 1; }
}

/* A bright streak sweeping the filled bar, also tied to the tier speed, so the meter reads as
   increasingly "charged" as the multiplier climbs. */
.sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, transparent 25%, rgba(255, 255, 255, 0.6) 50%, transparent 75%);
  transform: translateX(-100%);
  animation: sheen-sweep var(--pulse-speed, 1.6s) linear infinite;
}

@keyframes sheen-sweep {
  to { transform: translateX(100%); }
}

/* Tier-up: a quick spring on the multiplier label. */
.tier-pop {
  animation: tier-pop 0.45s cubic-bezier(0.18, 1.4, 0.4, 1) 1;
}

@keyframes tier-pop {
  0% { transform: scale(1); }
  45% { transform: scale(1.6); }
  100% { transform: scale(1); }
}

/* Reset: shake the whole meter and flash it red as the chain shatters. */
.momentum.is-reset {
  animation: momentum-shatter 0.5s ease-out 1;
}

@keyframes momentum-shatter {
  0% { transform: translateX(0); filter: drop-shadow(0 0 10px rgba(239, 68, 68, 0.8)); }
  15% { transform: translateX(-5px); }
  30% { transform: translateX(5px); }
  45% { transform: translateX(-4px); }
  60% { transform: translateX(4px); }
  75% { transform: translateX(-2px); }
  100% { transform: translateX(0); filter: drop-shadow(0 0 0 rgba(239, 68, 68, 0)); }
}
</style>
